import React, { useCallback, useEffect, useState } from "react";
import { Patient } from "../../PatientCard";
import PrescriptionHistory from "./PrescriptionHistory";
import PrescriptionModal from "./PrescriptionModal";
import PrescriptionService, {
  Prescription,
} from "../../../../services/prescriptionService";

interface PatientPrescriptionsTabProps {
  patient: Patient;
}

const PatientPrescriptionsTab: React.FC<PatientPrescriptionsTabProps> = ({
  patient,
}) => {
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [showPrescriptionHistory, setShowPrescriptionHistory] = useState(false);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  const handlePrescriptionCreated = (prescriptionId: number) => {
    // alert("Prescription created successfully!");
    setShowPrescriptionModal(false);
    // Trigger refresh for both history and stats
    setRefreshTrigger((prev) => prev + 1);
    loadPrescriptions();
  };

  const loadPrescriptions = useCallback(async () => {
    if (!patient.id) return;

    setStatsLoading(true);
    try {
      const result = await PrescriptionService.getPatientPrescriptions(
        patient.id
      );
      setPrescriptions(result);
    } catch (error) {
      console.error("Failed to load prescriptions for stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [patient.id]);

  // Add useEffect to load data when component mounts
  useEffect(() => {
    loadPrescriptions();
  }, [loadPrescriptions]);

  const handleGeneratePDF = (prescriptionId: number) => {
    // PDF generation logic
  };

  const handleSendWhatsApp = (prescriptionId: number) => {
    // WhatsApp sending logic
  };

  const calculateStats = () => {
    const activePrescriptions = prescriptions.filter(
      (p) => p.status === "active"
    );
    const completedPrescriptions = prescriptions.filter(
      (p) => p.status === "completed"
    );
    const totalMedicines = prescriptions.reduce(
      (sum, p) => sum + p.items.length,
      0
    );

    // Recent prescriptions (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentPrescriptions = prescriptions.filter(
      (p) => new Date(p.prescription_date) >= thirtyDaysAgo
    );

    // PDF/WhatsApp stats
    const pdfGenerated = prescriptions.filter((p) => p.pdf_generated).length;
    const whatsappSent = prescriptions.filter((p) => p.whatsapp_sent).length;

    return {
      total: prescriptions.length,
      active: activePrescriptions.length,
      completed: completedPrescriptions.length,
      totalMedicines,
      recent: recentPrescriptions.length,
      pdfGenerated,
      whatsappSent,
    };
  };

  const containerStyle: React.CSSProperties = {
    padding: "16px",
    height: "100%",
    overflow: "auto",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "24px",
    padding: "20px",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const headerContentStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "12px",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: "12px 16px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#8b5cf6" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
    minWidth: "fit-content",
  });

  const titleStyle: React.CSSProperties = {
    fontSize: "18px",
    fontWeight: "600",
    margin: 0,
    marginBottom: "4px",
    color: "#1f2937",
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: "14px",
    color: "#6b7280",
    margin: 0,
    lineHeight: "1.4",
  };

  const alertStyle: React.CSSProperties = {
    background: "#fef3c7",
    border: "1px solid #f59e0b",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "24px",
  };

  const statsContainerStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const statsGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "16px",
  };

  const statItemStyle: React.CSSProperties = {
    textAlign: "center",
    padding: "8px",
  };

  const statNumberStyle: React.CSSProperties = {
    fontSize: "20px",
    fontWeight: "700",
    marginBottom: "4px",
  };

  const statLabelStyle: React.CSSProperties = {
    fontSize: "11px",
    color: "#6b7280",
    lineHeight: "1.2",
  };

  return (
    <>
      <div style={containerStyle}>
        {/* Header with Action Buttons */}
        <div style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={{ flex: "1", minWidth: "200px" }}>
              <h3 style={titleStyle}>💊 Prescription Management</h3>
              <p style={subtitleStyle}>
                Create and manage prescriptions for{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </p>
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button
                style={buttonStyle("primary")}
                onClick={() => setShowPrescriptionModal(true)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#7c3aed")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#8b5cf6")
                }
              >
                💊 New Prescription
              </button>
            </div>
          </div>
        </div>

        {/* Patient Alerts for Prescriptions */}
        {(patient.allergies?.length || patient.chronic_conditions?.length) && (
          <div style={alertStyle}>
            <h4
              style={{
                color: "#92400e",
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
                margin: 0,
              }}
            >
              ⚠️ Important Medical Information
            </h4>
            {patient.allergies?.length && (
              <div
                style={{
                  color: "#92400e",
                  fontSize: "13px",
                  marginBottom: "4px",
                  wordBreak: "break-word",
                }}
              >
                <strong>Allergies:</strong> {patient.allergies.join(", ")}
              </div>
            )}
            {patient.chronic_conditions?.length && (
              <div
                style={{
                  color: "#92400e",
                  fontSize: "13px",
                  wordBreak: "break-word",
                }}
              >
                <strong>Chronic Conditions:</strong>{" "}
                {patient.chronic_conditions.join(", ")}
              </div>
            )}
          </div>
        )}

        {/* Inline Prescription History */}
        <PrescriptionHistory
          patient={patient}
          isOpen={true}
          onClose={() => {}} // Empty function since we don't want to close it
          refreshTrigger={refreshTrigger}
          onGeneratePDF={handleGeneratePDF}
          onSendWhatsApp={handleSendWhatsApp}
        />

        {/* Prescription Statistics */}
        <div style={statsContainerStyle}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            📊 Prescription Overview
          </h4>

          {statsLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #e2e8f0",
                  borderTop: "2px solid #8b5cf6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }}
              />
            </div>
          ) : (
            <div style={statsGridStyle}>
              {(() => {
                const stats = calculateStats();
                const statItems = [
                  {
                    value: stats.total,
                    label: "Total Prescriptions",
                    color: "#8b5cf6",
                  },
                  {
                    value: stats.active,
                    label: "Active",
                    color: "#10b981",
                  },
                  {
                    value: stats.completed,
                    label: "Completed",
                    color: "#6b7280",
                  },
                  {
                    value: stats.totalMedicines,
                    label: "Total Medicines",
                    color: "#3b82f6",
                  },
                  {
                    value: stats.recent,
                    label: "Recent (30 days)",
                    color: "#f59e0b",
                  },
                  {
                    value: stats.pdfGenerated,
                    label: "PDFs Generated",
                    color: "#ef4444",
                  },
                  {
                    value: stats.whatsappSent,
                    label: "WhatsApp Sent",
                    color: "#06b6d4",
                  },
                ];

                return statItems.map((item, index) => (
                  <div key={index} style={statItemStyle}>
                    <div
                      style={{
                        ...statNumberStyle,
                        color: item.color,
                      }}
                    >
                      {item.value}
                    </div>
                    <div style={statLabelStyle}>{item.label}</div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Prescription Modal */}
      <PrescriptionModal
        patient={patient}
        isOpen={showPrescriptionModal}
        onClose={() => setShowPrescriptionModal(false)}
        onPrescriptionCreated={handlePrescriptionCreated}
      />

      {/* Add CSS for animations */}
      <style>
        {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}
      </style>
    </>
  );
};

export default PatientPrescriptionsTab;
